// =====================================================
// TRADER DISCIPLINE INDEX (TDI) - TYPE DEFINITIONS
// A FICO-like credit score for trading discipline (300-850)
// =====================================================

export type TDITier = 'EXCELLENT' | 'GOOD' | 'FAIR' | 'DEVELOPING';
export type TDITrend = 'IMPROVING' | 'STABLE' | 'DECLINING' | 'NEW';

export interface TDIComponents {
  capital_preservation: number;
  decision_consistency: number;
  risk_avoidance: number;
  behavioral_stability: number;
}

// TDI v2 - BTDI and CETDI breakdown
export interface BTDIComponents {
  capital_preservation_behavior: number;
  decision_consistency: number;
  risk_avoidance: number;
  behavioral_stability: number;
}

export interface CETDIComponents {
  capital_preservation_score: number;
  growth_score: number;
  good_skips_usd: number;
  bad_skips_usd: number;
}

export interface TDIFactorDetail {
  score: number;
  weight: number;
  [key: string]: number | string | undefined;
}

export interface TDIFactors {
  capital_preservation: TDIFactorDetail;
  decision_consistency: TDIFactorDetail;
  risk_avoidance: TDIFactorDetail;
  behavioral_stability: TDIFactorDetail;
}

export interface TDIScore {
  success: boolean;
  tdi_score: number | null;
  tier?: TDITier;
  trend?: TDITrend;
  trend_change?: number;
  components?: TDIComponents;
  factors?: TDIFactors;
  decisions_counted?: number;
  period_days?: number;
  message?: string;
  decisions_needed?: number;
  error?: string;
  // TDI v2 fields
  btdi_score?: number;
  cetdi_score?: number;
  btdi_components?: BTDIComponents;
  cetdi_components?: CETDIComponents;
  directional_bias_adjustment?: number;
  explanation?: string[];
}

export interface TDIHistoryEntry {
  date: string;
  score: number;
  trend: TDITrend;
  trend_change: number;
  components: TDIComponents;
}

export interface TDIStats {
  avg_score: number | null;
  high_score: number | null;
  low_score: number | null;
  current_score: number | null;
  total_entries: number;
}

export interface TDIHistory {
  success: boolean;
  history: TDIHistoryEntry[];
  stats: TDIStats;
  error?: string;
}

// Get tier from score
export function getTierFromScore(score: number): TDITier {
  if (score >= 750) return 'EXCELLENT';
  if (score >= 650) return 'GOOD';
  if (score >= 550) return 'FAIR';
  return 'DEVELOPING';
}

// Get tier color - Purple/Cyan theme
export function getTierColor(tier: TDITier): string {
  switch (tier) {
    case 'EXCELLENT': return 'text-emerald-400';
    case 'GOOD': return 'text-cyan-400';
    case 'FAIR': return 'text-purple-400';
    case 'DEVELOPING': return 'text-gray-400';
  }
}

// Get tier background color - Purple/Cyan theme
export function getTierBgColor(tier: TDITier): string {
  switch (tier) {
    case 'EXCELLENT': return 'bg-emerald-500/10 border-emerald-500/30';
    case 'GOOD': return 'bg-cyan-500/10 border-cyan-500/30';
    case 'FAIR': return 'bg-purple-500/10 border-purple-500/30';
    case 'DEVELOPING': return 'bg-gray-800/50 border-gray-700';
  }
}

// Get trend icon
export function getTrendIcon(trend: TDITrend): string {
  switch (trend) {
    case 'IMPROVING': return '↑';
    case 'DECLINING': return '↓';
    case 'STABLE': return '→';
    case 'NEW': return '★';
  }
}

// Get trend color
export function getTrendColor(trend: TDITrend): string {
  switch (trend) {
    case 'IMPROVING': return 'text-green-500';
    case 'DECLINING': return 'text-red-500';
    case 'STABLE': return 'text-muted-foreground';
    case 'NEW': return 'text-primary';
  }
}

// Format score for display
export function formatTDIScore(score: number | null): string {
  if (score === null) return '---';
  return score.toString();
}

// Get component label
export function getComponentLabel(key: keyof TDIComponents): string {
  switch (key) {
    case 'capital_preservation': return 'Capital Preservation';
    case 'decision_consistency': return 'Decision Consistency';
    case 'risk_avoidance': return 'Risk Avoidance';
    case 'behavioral_stability': return 'Behavioral Stability';
  }
}

// Get component weight
export function getComponentWeight(key: keyof TDIComponents): number {
  switch (key) {
    case 'capital_preservation': return 35;
    case 'decision_consistency': return 30;
    case 'risk_avoidance': return 20;
    case 'behavioral_stability': return 15;
  }
}
